import { Suspense, useEffect, useState, useRef } from "react";
import styled from "styled-components";
import { Canvas, useThree } from "@react-three/fiber";
import * as THREE from "three";
import { Text } from "@react-three/drei";
import { XR, Controllers, startSession, useXR } from "@react-three/xr";
import XrControls from "./xr/XrControls";
import XrFloorRecovery from "./xr/XrFloorRecovery";
import XrRoomLighting from "./xr/XrRoomLighting";
import CarContactShadow from "./xr/CarContactShadow";
import { supportsContactShadow } from "./xr/roomLightingMath";
import XrHandModels from "./xr/XrHandModels";
import { initialSettings, setCarOnly, setPowertrain, renderingPolicy } from "./xr/interactionPolicy";
import {
  getPreferredImmersiveSessionMode,
  getXrEnterLabel,
} from "../../util/xrSessionSupport";
import TunnelContainer from "../webgl/Car/Tunnel/TunnelContainer";
import DayXrEnvironment from "../webgl/DayXrEnvironment";
import ToyotaCrownShowroomModel from "./ToyotaCrownShowroomModel";
import useFogControlStore from "../../state/fogControls";
import Lights from "../webgl/Car/CarFlip/Lights";
const SHOWROOM_ENVIRONMENT_X_OFFSET = 0;
const SHOWROOM_ENVIRONMENT_Z_OFFSET = 0;
const SHOWROOM_FLOOR_OFFSET = 0;
const SHOWROOM_ENVIRONMENT_ROTATION = Math.PI / 2;
const SHOWROOM_XR_SPAWN_POSITION = [-2.2, 0, 1.4];
const SHOWROOM_XR_LOOK_TARGET = [0, 1.15, 0];
const XR_INSTRUCTION_BOARD_POSITION = [3.1, 1.72, -3.35];
const XR_INSTRUCTION_BOARD_SCALE = [3.2, 1.8, 1];
const XR_INSTRUCTION_BOARD_ROTATION_Y = (-38 * Math.PI) / 180;
export default function ShowroomCanvas({
  onBack,
  trimLabel,
  backLabel = "Back To Trims",
  titleOverride,
  descriptionOverride,
  hideOverlay = false,
  onCanvasReady,
  renderSceneContent = true,
}) {
  const [vrPending, setVrPending] = useState(false);
  const [entryError, setEntryError] = useState(null);
  const [showUnsupportedPopup, setShowUnsupportedPopup] = useState(false);
  const [sessionMode, setSessionMode] = useState(null);
  const [sceneEpoch, setSceneEpoch] = useState(0);

  useEffect(() => {
    let cancelled = false;

    const checkVrSupport = async () => {
      const supportedMode = await getPreferredImmersiveSessionMode(navigator?.xr);

      if (!cancelled) {
        setSessionMode(supportedMode);
      }
    };

    checkVrSupport();

    return () => {
      cancelled = true;
    };
  }, []);

  const handleEnterVr = async () => {
    if (vrPending) {
      return;
    }

    try {
      const resolvedMode =
        sessionMode ??
        (await getPreferredImmersiveSessionMode(navigator?.xr));

      if (!resolvedMode) {
        setShowUnsupportedPopup(true);
        return;
      }

      setSessionMode(resolvedMode);
      setEntryError(null);
      setVrPending(true);

      const session = await startSession(resolvedMode, {
        optionalFeatures: ["local-floor", "bounded-floor", "hand-tracking", "light-estimation", "plane-detection", "hit-test"],
      });

      if (session) {
        session.addEventListener(
          "end",
          () => {
            setVrPending(false);
            setSceneEpoch((current) => current + 1);
          },
          { once: true }
        );
      } else {
        setVrPending(false);
        setShowUnsupportedPopup(true);
      }
    } catch (error) {
      console.error("Toyota XR entry failed", error);
      setVrPending(false);
      setEntryError("XR could not start. Check browser permissions, then select Enter XR to retry.");
    }
  };

  return (
    <StageRoot>
      <Canvas
        key={sceneEpoch}
        dpr={[1, 1.5]}
        gl={{ antialias: true, alpha: true }}
        camera={{ position: [0, 1.65, 7.2], fov: 32, near: 0.1, far: 320 }}
        style={{ position: "absolute", inset: 0 }}
        onCreated={() => {
          onCanvasReady?.();
        }}
      >
        <XR referenceSpace="local-floor">
          {renderSceneContent ? <ShowroomScene /> : null}
        </XR>
      </Canvas>

      {!hideOverlay && (
        <Overlay>
          <div className="meta">
            <button type="button" className="ghost" onClick={onBack}>
              {backLabel}
            </button>
            <div className="copy">
              <span className="eyebrow">Showroom</span>
              <h2>{titleOverride ?? `${trimLabel.toUpperCase()} in WebXR`}</h2>
              <p>
                {descriptionOverride ??
                  "Use controllers, or raise your left wrist like checking a watch. Point and pinch or touch the menu to customize your Crown."}
              </p>
            </div>
          </div>

          {entryError && <p role="alert">{entryError}</p>}
          {(
            <div className="actions">
              <button
                type="button"
                className="primary xr-button"
                onClick={handleEnterVr}
                disabled={vrPending}
              >
                {vrPending ? "Entering XR..." : getXrEnterLabel(sessionMode)}
              </button>
            </div>
          )}
        </Overlay>
      )}

      {showUnsupportedPopup && (
        <UnsupportedVrModal role="dialog" aria-modal="true">
          <div
            className="backdrop"
            onClick={() => setShowUnsupportedPopup(false)}
          />
          <div className="panel">
            <span className="eyebrow">WebXR Required</span>
            <h3>Open This On A Supported Device</h3>
            <p>
              This browser cannot start an immersive WebXR session. Open this
              experience in an XR-capable browser to use hands or controllers.
            </p>
            <button
              type="button"
              className="dismiss"
              onClick={() => setShowUnsupportedPopup(false)}
            >
              Continue On Screen
            </button>
          </div>
        </UnsupportedVrModal>
      )}
    </StageRoot>
  );
}

function ShowroomScene() {
  const dayFog = useFogControlStore((state) => state.dayFog);
  const xrCarPosition = useFogControlStore((state) => state.xrCarPosition);
  const session = useXR((state) => state.session);
  const roomLighting = useRef({ environment: null, status: "off" });
  const [settings, setSettings] = useState(initialSettings);
  useEffect(() => {
    setSettings(initialSettings());
    if (session) console.info("Toyota XR session", { environmentBlendMode: session.environmentBlendMode });
  }, [session]);
  const policy = renderingPolicy(settings.carOnly, session?.environmentBlendMode);

  return (
    <>
      <XrSpawnAnchor />
      <XrFloorRecovery />
      <XrRoomLighting enabled={!!session && settings.carOnly} lighting={roomLighting} />
      <XrBackground policy={policy} />
      {!settings.carOnly && dayFog.enabled && <fog attach="fog" args={[dayFog.color, dayFog.near, dayFog.far]} />}
      <Controllers rayMaterial={{ transparent: true, opacity: 0 }} />
      {session && <Suspense fallback={null}><XrHandModels /></Suspense>}
      <Suspense fallback={null}>
        {!settings.carOnly && <group name="showroom-environment">
          <DayXrEnvironment radius={220} mode="day" />
          <TunnelContainer floorOffset={SHOWROOM_FLOOR_OFFSET} xOffset={SHOWROOM_ENVIRONMENT_X_OFFSET} zOffset={SHOWROOM_ENVIRONMENT_Z_OFFSET} rotationY={SHOWROOM_ENVIRONMENT_ROTATION} visibleOverride forceVisible />
          <XrInstructionBoard />
        </group>}
        <ToyotaCrownShowroomModel powertrain={settings.powertrain} carOnly={settings.carOnly} roomLighting={roomLighting} xOffset={xrCarPosition.x} floorOffset={xrCarPosition.y} depthOffset={xrCarPosition.z} />
        {settings.carOnly && supportsContactShadow(session?.environmentBlendMode) && <CarContactShadow x={xrCarPosition.x} z={xrCarPosition.z} />}
        {!settings.carOnly && <Lights />}
        {session && <XrControls key="xr-controls" settings={settings} seeThrough={policy.seeThrough}
          onPowertrainChange={(value) => setSettings((current) => setPowertrain(current, value))}
          onCarOnlyChange={(value) => setSettings((current) => setCarOnly(current, value))} />}
      </Suspense>
    </>
  );
}

function XrBackground({ policy }) {
  const { scene, gl } = useThree();
  useEffect(() => {
    const oldBackground = scene.background;
    const oldClearColor = gl.getClearColor(new THREE.Color()).clone();
    const oldClearAlpha = gl.getClearAlpha();
    scene.background = policy.background === null ? null : new THREE.Color(policy.background);
    gl.setClearColor(0x000000, policy.clearAlpha);
    return () => { scene.background = oldBackground; gl.setClearColor(oldClearColor, oldClearAlpha); };
  }, [scene, gl, policy.background, policy.clearAlpha]);
  return null;
}

function XrSpawnAnchor() {
  const player = useXR((state) => state.player);
  const isPresenting = useXR((state) => state.isPresenting);

  useEffect(() => {
    if (!isPresenting || !player) {
      return;
    }

    player.position.set(...SHOWROOM_XR_SPAWN_POSITION);
    const target = new THREE.Vector3(...SHOWROOM_XR_LOOK_TARGET);
    const direction = target.sub(player.position);
    // Cameras look down -Z; keep the existing spawn location facing the car.
    const yaw = Math.atan2(-direction.x, -direction.z);
    player.rotation.set(0, yaw, 0);
    player.updateMatrixWorld();
  }, [isPresenting, player]);

  return null;
}

function XrInstructionBoard() {
  return (
    <group
      position={[
        SHOWROOM_ENVIRONMENT_X_OFFSET,
        SHOWROOM_FLOOR_OFFSET,
        SHOWROOM_ENVIRONMENT_Z_OFFSET,
      ]}
      rotation={[0, SHOWROOM_ENVIRONMENT_ROTATION, 0]}
    >
      <group
        position={XR_INSTRUCTION_BOARD_POSITION}
        rotation={[0, XR_INSTRUCTION_BOARD_ROTATION_Y, 0]}
        scale={XR_INSTRUCTION_BOARD_SCALE}
      >
      <mesh>
        <planeGeometry args={[1, 1]} />
        <meshBasicMaterial
          color="#000000"
          toneMapped={false}
          side={THREE.DoubleSide}
        />
      </mesh>
      <Text position={[0, 0, 0.002]} fontSize={0.041} maxWidth={0.9} textAlign="center" color="#ffffff" lineHeight={1.5}>
        {"YOUR CROWN, YOUR WAY\n\nRaise your left wrist to open the menu.\nPoint and pinch, or touch a button.\nControllers: left grip opens the menu.\nRight grip + trigger teleports.\n\nCar-only hides the showroom.\nLens dimming stays in system controls."}
      </Text>
      </group>
    </group>
  );
}

const StageRoot = styled.section`
  position: relative;
  height: 100vh;
`;

const Overlay = styled.div`
  position: absolute;
  inset: 0;
  z-index: 4;
  display: flex;
  justify-content: flex-start;
  align-items: flex-end;
  padding: 112px 32px 36px;
  pointer-events: none;

  .meta,
  .actions {
    pointer-events: auto;
  }

  .meta {
    max-width: 430px;
    display: flex;
    flex-direction: column;
    gap: 18px;
  }

  .actions {
    position: absolute;
    top: calc(var(--app-header-height, 104px) + 16px);
    right: 32px;
    display: flex;
    justify-content: flex-end;
  }

  .eyebrow {
    display: block;
    margin-bottom: 10px;
    font-size: 0.72rem;
    letter-spacing: 0.26em;
    text-transform: uppercase;
    color: rgba(255, 255, 255, 0.62);
  }

  h2 {
    margin: 0 0 10px;
    font-size: clamp(2rem, 4vw, 3.4rem);
    line-height: 0.96;
    letter-spacing: 0.06em;
    text-transform: uppercase;
  }

  p {
    margin: 0;
    font-size: 1rem;
    line-height: 1.5;
    color: rgba(255, 255, 255, 0.78);
  }

  button {
    height: 52px;
    border-radius: 999px;
    border: 0;
    padding: 0 24px;
    font-size: 0.85rem;
    font-weight: 700;
    letter-spacing: 0.12em;
    text-transform: uppercase;
    cursor: pointer;
  }

  .primary {
    background: #f5f3ef;
    color: #050505;
  }

  .ghost {
    width: fit-content;
    background: rgba(255, 255, 255, 0.08);
    color: #f5f3ef;
    border: 1px solid rgba(255, 255, 255, 0.14);
  }

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: stretch;
    justify-content: space-between;
    padding: 116px 18px 24px;

    .actions {
      top: calc(var(--app-header-height, 60px) + 24px);
      right: 18px;
    }

    .actions button,
    .ghost {
      width: auto;
    }

    button {
      height: 44px;
      padding: 0 20px;
      font-size: 0.8rem;
    }
  }
`;

const UnsupportedVrModal = styled.div`
  position: fixed;
  inset: 0;
  z-index: 12000;
  pointer-events: auto;

  .backdrop {
    position: absolute;
    inset: 0;
    background: rgba(0, 0, 0, 0.68);
    backdrop-filter: blur(10px);
  }

  .panel {
    position: absolute;
    left: 50%;
    top: 50%;
    width: min(520px, calc(100vw - 32px));
    padding: 28px 28px 24px;
    border-radius: 28px;
    transform: translate(-50%, -50%);
    background: rgba(8, 8, 10, 0.96);
    box-shadow:
      0 28px 80px rgba(0, 0, 0, 0.42),
      inset 0 0 0 1px rgba(255, 255, 255, 0.08);
    color: #f5f3ef;
  }

  .eyebrow {
    display: block;
    margin-bottom: 12px;
    color: rgba(255, 255, 255, 0.54);
    font-size: 0.72rem;
    font-weight: 700;
    letter-spacing: 0.24em;
    text-transform: uppercase;
  }

  h3 {
    margin: 0 0 14px;
    font-size: clamp(1.7rem, 4vw, 2.5rem);
    line-height: 0.98;
    letter-spacing: 0.04em;
    text-transform: uppercase;
  }

  p {
    margin: 0;
    color: rgba(255, 255, 255, 0.78);
    font-size: 1rem;
    line-height: 1.5;
  }

  .dismiss {
    margin-top: 22px;
    height: 48px;
    border: 0;
    border-radius: 999px;
    padding: 0 22px;
    background: #ffffff;
    color: #050505;
    font-size: 0.84rem;
    font-weight: 700;
    letter-spacing: 0.12em;
    text-transform: uppercase;
    cursor: pointer;
  }

  @media (max-width: 768px) {
    .panel {
      padding: 22px 18px 18px;
    }

    .dismiss {
      width: 100%;
    }
  }
`;
